import { captureException } from '@sentry/react';
import deLocale from '@uploadcare/file-uploader/locales/file-uploader/de.js';
import enLocale from '@uploadcare/file-uploader/locales/file-uploader/en.js';
import type { OutputCollectionState, OutputFileEntry, UploadCtxProvider } from '@uploadcare/react-uploader';
import { defineLocale, FileUploaderRegular } from '@uploadcare/react-uploader';
import '@uploadcare/react-uploader/core.css';
import { useCallback, useMemo, useRef, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { useToast } from '../../../components/ui/use-toast.tsx';
import { UPLOADCARE_CDN_NAME, UPLOADCARE_PUBLIC_KEY } from '../../../lib/constants.ts';
import { useAppState } from '../../../lib/hooks/use-app-state.ts';
import { useLocale } from '../../../lib/hooks/use-locale.ts';
import type { CrmAttachmentInput } from '../../../server/dto/crm-attachment-dto.ts';
import { api } from '../../../utils/api.ts';
import { useAnalytics } from '../../analytics/hooks/use-analytics.ts';

defineLocale('de', deLocale);
defineLocale('en', enLocale);

function toAttachmentFields(entry: OutputFileEntry<'success'>): CrmAttachmentInput {
    return {
        externalUuid: entry.uuid,
        cdnUrl: entry.cdnUrl,
        cdnUrlModifiers: entry.cdnUrlModifiers,
        name: entry.name,
        size: entry.size,
        mimeType: entry.mimeType,
        isImage: entry.isImage,
        s3Url: entry.fileInfo.s3Url ?? null,
        s3Bucket: entry.fileInfo.s3Bucket ?? null,
    };
}

export type UseFileUploaderOptions = {
    value: CrmAttachmentInput[];
    onChange: (files: CrmAttachmentInput[]) => void;
    analyticsContext: 'task' | 'note';
};

export type UseFileUploaderResult = {
    open: () => void;
    remove: (file: CrmAttachmentInput) => void;
    edit: (file: CrmAttachmentInput) => void;
    download: (file: CrmAttachmentInput) => void;
    isReady: boolean;
    uploaderElement: ReactNode;
};

export function useFileUploader({
    value,
    onChange,
    analyticsContext,
}: UseFileUploaderOptions): UseFileUploaderResult {
    const { t, locale } = useLocale('common');
    const { tenantId, platoUser } = useAppState();
    const { data: uploadSignature, isLoading } = api.platoUser.query.uploadcare.getUploadSignature.useQuery();
    const { mutateAsync: signCdnUrl } = api.platoUser.command.uploadcare.signCdnUrl.useMutation();
    const uploaderRef = useRef<UploadCtxProvider | null>(null);
    const sendEvent = useAnalytics();
    const { toast } = useToast();

    const secureDeliveryProxyUrlResolver = useCallback(
        async (cdnUrl: string): Promise<string> => {
            const { signedUrl } = await signCdnUrl({ cdnUrl });
            return signedUrl;
        },
        [signCdnUrl],
    );

    const uploadMetadata = useMemo(
        () => ({
            tenantId,
            userId: platoUser.id,
        }),
        [tenantId, platoUser.id],
    );

    const handleDoneClick = useCallback(
        (event: OutputCollectionState) => {
            const existingUuids = new Set(value.map((f) => f.externalUuid));
            const newEntries = event.successEntries.filter((entry) => !existingUuids.has(entry.uuid));

            for (const entry of newEntries) {
                sendEvent('attachment_uploaded', { context: analyticsContext, file_type: entry.mimeType });
            }

            onChange([...value, ...newEntries.map(toAttachmentFields)]);
        },
        [onChange, sendEvent, value, analyticsContext],
    );

    const handleFileUploadFailed = useCallback(
        (entry: OutputFileEntry<'failed'>) => {
            sendEvent('attachment_upload_failed', { context: analyticsContext, file_type: entry.mimeType });
            captureException(new Error('Uploadcare file upload failed'), {
                extra: {
                    externalUuid: entry.uuid,
                    mimeType: entry.mimeType,
                    errors: entry.errors,
                },
            });
            toast({ icon: 'warning', title: t('attachments__load_error') });
        },
        [sendEvent, analyticsContext, toast, t],
    );

    const handleFileUrlChanged = useCallback(
        (entry: OutputFileEntry<'success'>) => {
            onChange(
                value.map((file) =>
                    file.externalUuid === entry.uuid ? { ...file, ...toAttachmentFields(entry) } : file,
                ),
            );

            sendEvent('attachment_edited', { context: analyticsContext, file_type: entry.mimeType });
        },
        [value, onChange, sendEvent, analyticsContext],
    );

    const remove = useCallback(
        (fileToRemove: CrmAttachmentInput) => {
            const uploaderApi = uploaderRef.current?.getAPI();
            const collectionState = uploaderApi?.getOutputCollectionState();
            const entry = collectionState?.allEntries.find(
                (entry) => entry.uuid === fileToRemove.externalUuid,
            );

            if (entry) {
                uploaderApi?.removeFileByInternalId(entry.internalId);
            }

            sendEvent('attachment_deleted', { context: analyticsContext, file_type: fileToRemove.mimeType });

            onChange(value.filter((file) => file.externalUuid !== fileToRemove.externalUuid));
        },
        [value, onChange, sendEvent, analyticsContext],
    );

    const edit = useCallback(
        (fileToEdit: CrmAttachmentInput) => {
            const uploaderApi = uploaderRef.current?.getAPI();
            if (!uploaderApi) {
                captureException(new Error('Uploadcare API unavailable when opening editor'), {
                    extra: { attachmentId: fileToEdit.attachmentId },
                });
                toast({ icon: 'warning', title: t('attachments__load_error') });
                return;
            }

            // Reuse an already-hydrated entry if present; otherwise lazy-hydrate from the CDN url.
            const existing = uploaderApi
                .getOutputCollectionState()
                .successEntries.find((entry) => entry.uuid === fileToEdit.externalUuid);

            let internalId: string;
            try {
                internalId =
                    existing?.internalId ??
                    uploaderApi.addFileFromCdnUrl(fileToEdit.cdnUrl, {
                        silent: true,
                        source: 'js-api',
                    }).internalId;
            } catch (error) {
                captureException(error, {
                    extra: {
                        attachmentId: fileToEdit.attachmentId,
                        cdnUrl: fileToEdit.cdnUrl,
                    },
                });
                toast({ icon: 'warning', title: t('attachments__load_error') });
                return;
            }

            uploaderApi.setCurrentActivity('cloud-image-edit', { internalId });
            uploaderApi.setModalState(true);
        },
        [toast, t],
    );

    const download = useCallback(
        (file: CrmAttachmentInput) => {
            sendEvent('attachment_downloaded', { context: analyticsContext, file_type: file.mimeType });
        },
        [sendEvent, analyticsContext],
    );

    const open = useCallback(() => {
        const uploaderApi = uploaderRef.current?.getAPI();

        if (uploaderApi) {
            // Drop any lazy-hydrated entries from previous edits so the flow starts clean.
            uploaderApi.removeAllFiles();
            uploaderApi.initFlow();
        }
    }, []);

    const isReady = !isLoading && !!uploadSignature;

    const uploaderElement = uploadSignature
        ? createPortal(
              <FileUploaderRegular
                  apiRef={uploaderRef}
                  pubkey={String(UPLOADCARE_PUBLIC_KEY)}
                  cdnCname={String(UPLOADCARE_CDN_NAME)}
                  classNameUploader="uc-light"
                  sourceList="local, camera"
                  userAgentIntegration="llm-react"
                  filesViewMode="list"
                  localeName={locale.split('-')[0]} // Use 'en' from 'en-US', etc.
                  secureSignature={uploadSignature.signature}
                  secureExpire={String(uploadSignature.expire)}
                  secureDeliveryProxyUrlResolver={secureDeliveryProxyUrlResolver}
                  metadata={uploadMetadata}
                  onDoneClick={handleDoneClick}
                  onFileUploadFailed={handleFileUploadFailed}
                  onFileUrlChanged={handleFileUrlChanged}
                  headless
              />,
              document.body,
          )
        : null;

    return { open, remove, edit, download, isReady, uploaderElement };
}
